import { BellIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import type { FeedItem } from '../lib/types';



// Unseen-count badge only — the feed itself lives on Domov (tab Aktivita),
// so clicking the bell navigates there and marks the newest item as seen.
export const ActivityBell = ({ enabled, className }: { enabled?: boolean; className?: string }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const SEEN_KEY = `ww_seen_activity_id:${user?.id ?? 'anonymous'}`;
  const isEnabled = enabled ?? Boolean(user);

  const eventsQuery = useQuery({
    queryKey: ['events', 'bell'],
    queryFn: () => api.eventsList({ limit: 10 }),
    enabled: isEnabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const items = useMemo<FeedItem[]>(() => eventsQuery.data?.items ?? [], [eventsQuery.data]);
  const newestId = items.length > 0 ? items[0].id : 0;

  const [seenId, setSeenId] = useState(() => Number(localStorage.getItem(SEEN_KEY) || 0));

  useEffect(()=>{setSeenId(Number(localStorage.getItem(SEEN_KEY)||0));},[SEEN_KEY]);

  const hasUnread = useMemo(
    () => items.some((item) => item.id > seenId && item.actorUserId !== user?.id),
    [items, seenId, user?.id],
  );

  const handleClick = () => {
    if (newestId > 0) {
      localStorage.setItem(SEEN_KEY, String(newestId));
      setSeenId(newestId);
    }
    navigate('/?tab=aktivita');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        className ??
        'relative flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-black/[0.04] focus:outline-none dark:text-slate-300 dark:hover:bg-white/[0.05]'
      }
      aria-label="Aktivita"
      title="Aktivita"
    >
      <BellIcon className={className ? 'h-[18px] w-[18px]' : 'h-6 w-6'} />
      {hasUnread ? (
        <span
          className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-surface"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
};
